<template>
  <div class="max-w-[100vw] overflow-hidden relative">
    <Carousel
      ref="carouselRef"
      class="image-scroller-carousel"
      @mounted="onMounted"
      @slide-change="onSlideChange"
      @left-bound="onLeftBound"
      @right-bound="onRightBound"
      style="max-width: 100vw"
    >
      <Slide v-for="(image, index) in imageScrollerData" :key="index">
        <div class="flex flex-row items-stretch justify-stretch w-full h-[60vh] max-h-128 lg:bg-gray-900">
          <!-- Portrait orientation -->
          <div v-if="image.orientation === 'portrait'"
               class="w-full lg:mx-auto min-h-full flex flex-row items-center justify-start">
            <div
                class="flex lg:w-1/2 lg:text-left lg:items-end flex-col text-white text-left px-4 w-full absolute lg:relative z-[250]">
              <div class="w-full lg:max-w-screen-sm lg:pl-12 flex flex-col justify-center items-start">
                <h1 :class="image.titleClass" class="py-4 text-4xl">{{ image.title }}</h1>
                <h2 v-if="image.description" :class="image.descriptionClass" class="py-12 text-xl lg:max-w-screen-sm">
                  {{ image.description }}</h2>
                <div v-if="image.ctaText" class="flex flex-row justify-start items-center">
                  <a :href="image.ctaLink"
                     class="bg-green-400 hover:bg-green-600 text-white text-lg font-bold py-2 px-6 rounded flex items-center transition-all duration-300 ease-in-out group">
                    <span>{{ image.ctaText }}</span>
                    <span v-if="image.includeCtaArrow" class="ml-2 transition-transform duration-300 ease-in-out group-hover:translate-x-1">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </span>
                  </a>
                </div>
              </div>
            </div>
            <div
                class="bg-no-repeat bg-contain bg-center min-h-full lg:w-1/2 w-full z-[201]"
                :style="`background: linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.2)),  url('${image.src}') center/cover no-repeat`"></div>
          </div>
          
          <!-- Landscape orientation -->
          <div v-if="image.orientation === 'landscape'"
               class="w-full min-h-full bg-no-repeat bg-contain bg-center flex flex-row items-center justify-center"
               :style="`background: linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.3)), url('${image.src}') center/cover no-repeat`">
            <div class="text-white text-center px-4 max-w-screen-xl flex flex-col items-center justify-center">
              <h1 :class="image.titleClass" class="py-4 text-4xl">{{ image.title }}</h1>
              <h2 v-if="image.description" :class="image.descriptionClass" class="py-12 text-xl">{{ image.description }}</h2>
                <a v-if="image.ctaText" :href="image.ctaLink"
                   class="bg-green-400 hover:bg-green-600 text-white text-lg font-bold py-2 px-6 rounded flex items-center transition-all duration-300 ease-in-out group">
                  <span>{{ image.ctaText }}</span>
                  <span v-if="image.includeCtaArrow" class="ml-2 transition-transform duration-300 ease-in-out group-hover:translate-x-1">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                </span>
              </a>
            </div>
          </div>
        </div>
      </Slide>
    </Carousel>
    
    <!-- Navigation Dots -->
    <div v-if="showNavigation && imageScrollerData.length > 1" 
         class="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-[400] flex space-x-3">
      <button
        v-for="(image, index) in imageScrollerData"
        :key="index"
        @click="goToSlide(index)"
        class="min-w-[24px] min-h-[24px] flex items-center justify-center bg-transparent focus:outline-none"
        :aria-label="`Go to slide ${index + 1}`"
      >
        <span
          class="block w-3 h-3 rounded-full transition-all duration-300 border-accent border-2"
          :class="selectedDot === index
                ? 'scale-110 bg-none'
                : 'bg-accent bg-opacity-20 hover:bg-opacity-75 cursor-pointer'"
        ></span>
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { Carousel, Slide } from 'vue-snap'

export interface ImageProps {
  src: string;
  title: string;
  titleClass?: string;
  description?: string;
  descriptionClass?: string;
  ctaText?: string;
  ctaLink?: string;
  orientation: 'landscape' | 'portrait';
  includeCtaArrow?: boolean;
}

export default defineComponent({
  components: {
    Carousel,
    Slide
  },
  props: {
    imageScrollerData: {
      type: Array as () => ImageProps[],
      required: true
    },
    autoplayInterval: {
      type: Number,
      default: 5000
    },
    enableAutoplay: {
      type: Boolean,
      default: true
    },
    showNavigation: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      currentSlideIndex: 0,
      isAtLeftBound: false,
      isAtRightBound: false,
      autoplayTimer: null as NodeJS.Timeout | null,
      isHovering: false,
      isCarouselMounted: false,
      selectedDot: 0
    };
  },
  methods: {
    onMounted() {
      this.isCarouselMounted = true;
      this.startAutoplay();
    },
    onSlideChange(newIndex: number) {
      this.currentSlideIndex = newIndex;
      
      // Reset autoplay timer when slide changes (either by autoplay or manual navigation)
      if (this.enableAutoplay && !this.isHovering) {
        this.restartAutoplay();
      }
    },
    onLeftBound(isAtBound: boolean) {
      this.isAtLeftBound = isAtBound;
    },
    onRightBound(isAtBound: boolean) {
      this.isAtRightBound = isAtBound;
    },
    startAutoplay() {
      if (!this.enableAutoplay || this.imageScrollerData.length <= 1 || !this.isCarouselMounted || this.isHovering) return;
      
      this.autoplayTimer = setInterval(() => {
        if (!this.isHovering && this.$refs.carouselRef) {
          try {
            const carousel = this.$refs.carouselRef as any;
            if (carousel && typeof carousel.goToSlide === 'function') {
              const nextIndex = (this.currentSlideIndex + 1) % this.imageScrollerData.length;
              this.selectedDot = nextIndex;
              carousel.goToSlide(nextIndex);
            } else {
              console.warn('Carousel ref not available or changeSlide method not found');
            }
          } catch (error) {
            console.error('Error calling changeSlide:', error);
          }
        }
      }, this.autoplayInterval);
    },
    stopAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    },
    restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    },
    onMouseEnter() {
      this.isHovering = true;
      this.stopAutoplay();
    },
    onMouseLeave() {
      this.isHovering = false;
      this.startAutoplay();
    },
    // Expose carousel methods for external control
    goToSlide(index: number) {
      if (this.$refs.carouselRef && this.isCarouselMounted) {
        try {
          const carousel = this.$refs.carouselRef as any;
          if (carousel && typeof carousel.goToSlide === 'function') {
            this.selectedDot = index;
            carousel.goToSlide(index);
            // Reset autoplay timer when manually navigating
            if (this.enableAutoplay && !this.isHovering) {
              this.restartAutoplay();
            }
          } else {
            console.warn('Carousel ref not available or goToSlide method not found');
          }
        } catch (error) {
          console.error('Error calling goToSlide:', error);
        }
      }
    },
    changeSlide(direction: number) {
      if (this.$refs.carouselRef && this.isCarouselMounted) {
        try {
          const carousel = this.$refs.carouselRef as any;
          if (carousel && typeof carousel.changeSlide === 'function') {
            carousel.changeSlide(direction);
            // Reset autoplay timer when manually navigating
            if (this.enableAutoplay && !this.isHovering) {
              this.restartAutoplay();
            }
          } else {
            console.warn('Carousel ref not available or changeSlide method not found');
          }
        } catch (error) {
          console.error('Error calling changeSlide:', error);
        }
      }
    },
    nextSlide() {
      this.changeSlide(1);
    },
    previousSlide() {
      this.changeSlide(-1);
    }
  },
  mounted() {
    // Add event listeners for hover
    const carouselElement = this.$el;
    carouselElement.addEventListener('mouseenter', this.onMouseEnter);
    carouselElement.addEventListener('mouseleave', this.onMouseLeave);
  },
  beforeUnmount() {
    this.stopAutoplay();
    
    // Remove event listeners
    if (this.$el) {
      const carouselElement = this.$el;
      carouselElement.removeEventListener('mouseenter', this.onMouseEnter);
      carouselElement.removeEventListener('mouseleave', this.onMouseLeave);
    }
  },
  watch: {
    enableAutoplay(newVal: boolean) {
      if (newVal && !this.isHovering) {
        this.startAutoplay();
      } else {
        this.stopAutoplay();
      }
    },
    autoplayInterval() {
      if (this.enableAutoplay && !this.isHovering) {
        this.restartAutoplay();
      }
    }
  }
});
</script>

<style scoped>
/* VueSnap Carousel specific styles */
.image-scroller-carousel {
  width: 100%;
  max-width: 100vw;
}

:deep(.vs-carousel__wrapper) {
  height: auto;
}

:deep(.vs-carousel__slide) {
  flex: 0 0 100%;
  width: 100%;
}

/* Hide arrow navigation */
:deep(.vs-carousel__arrows) {
  display: none !important;
}

/* Ensure proper z-index for content */
.z-index-\[250\] {
  z-index: 250;
}

.z-index-\[201\] {
  z-index: 201;
}
</style> 